import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Upload, Plus, Users, CheckCircle, Shield, Eye, Mail, Trash2, X, Send, FileText, Download, Check, Pencil, Cloud, AlertTriangle } from "lucide-react";
import { API } from "../config";
import { StatusBadge } from "../components/shared/StatusBadge";
import { EditCustomerModal } from "../components/shared/EditCustomerModal";
import { getCustomerDisplayName } from "../utils/customerName";

/**
 * Visual badge that distinguishes a Business (B2B) customer from a
 * Private (B2C) one at-a-glance — prevents admin operators from
 * addressing a company by a first name, or accidentally sending a
 * "Hi Mario" template to a Ragione Sociale. Req. Zorzutti 09/06/2026.
 *
 * Business = orange dot, "Business" pill; Private = blue dot,
 * "Privato" pill. Kept tiny, dependency-free and accessible (aria-label).
 */
const CustomerTypeBadge = ({ type, compact = false }) => {
  const isBusiness = (type || 'private') === 'business';
  const cls = isBusiness
    ? 'bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40'
    : 'bg-sky-500/15 text-sky-300 border-sky-500/40';
  const dot = isBusiness ? 'bg-lojack-clause-400' : 'bg-sky-400';
  const label = isBusiness ? 'Business' : 'Privato';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      aria-label={`Tipo cliente: ${label}`}
      data-testid={`customer-type-badge-${isBusiness ? 'business' : 'private'}`}
      title={isBusiness ? 'Cliente Business (B2B) — usa Ragione Sociale' : 'Cliente Privato (B2C) — usa Nome e Cognome'}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
      {compact ? label[0] : label}
    </span>
  );
};

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx'))) setFile(f); else toast.error('Formato non supportato. Usa file .csv o .xlsx'); };
  const handleFileSelect = (e) => { const f = e.target.files[0]; if (f) setFile(f); };
  const downloadTemplate = async (fmt) => { try { const res = await axios.get(`${API}/customers/bulk/template/${fmt}`, { responseType: 'blob' }); const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = `template_clienti.${fmt}`; a.click(); URL.revokeObjectURL(url); toast.success(`Template ${fmt.toUpperCase()} scaricato`); } catch { toast.error('Errore nel download del template'); } };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/customers/bulk/upload?send_emails=${sendEmails}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (res.data.created > 0) toast.success(`${res.data.created} clienti creati con successo`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore durante l'upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2" data-testid="bulk-upload-title"><Upload size={20} className="text-lojack-red" />Upload Massivo Clienti</h2>
            <p className="text-sm text-gray-400 mt-1">Carica un file CSV o Excel con i dati dei clienti</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg transition-colors"><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {!result ? (
            <>
              <div className="bg-[#111] rounded-xl p-4 border border-gray-800">
                <p className="text-sm text-gray-400 mb-3">Scarica il template con le colonne corrette:</p>
                <div className="flex gap-2">
                  <button onClick={() => downloadTemplate('csv')} className="btn-lojack-outline text-sm flex items-center gap-2" data-testid="download-template-csv"><Download size={16} /> Template CSV</button>
                  <button onClick={() => downloadTemplate('xlsx')} className="btn-lojack-outline text-sm flex items-center gap-2" data-testid="download-template-xlsx"><Download size={16} /> Template Excel</button>
                </div>
              </div>
              <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${dragOver ? 'border-lojack-red bg-red-900/10' : file ? 'border-lojack-blue-600 bg-lojack-blue-900/10' : 'border-gray-700 hover:border-gray-500'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                onClick={() => document.getElementById('bulk-file-input').click()} data-testid="bulk-drop-zone">
                <input id="bulk-file-input" type="file" accept=".csv,.xlsx" onChange={handleFileSelect} className="hidden" />
                {file ? (
                  <div><FileText size={40} className="mx-auto mb-3 text-lojack-blue-500" /><p className="text-white font-medium">{file.name}</p><p className="text-gray-500 text-sm mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-red-400 text-sm mt-2 hover:underline">Rimuovi file</button></div>
                ) : (
                  <div><Upload size={40} className="mx-auto mb-3 text-gray-600" /><p className="text-gray-400">Trascina qui il file oppure <span className="text-lojack-red">clicca per selezionare</span></p><p className="text-gray-600 text-sm mt-1">Formati supportati: .csv, .xlsx</p></div>
                )}
              </div>
              <div className="flex items-center justify-between bg-[#111] rounded-xl p-4 border border-gray-800">
                <div><p className="text-white font-medium text-sm">Invia email di benvenuto</p><p className="text-gray-500 text-xs mt-1">Invia automaticamente l'email a tutti i clienti creati</p></div>
                <button onClick={() => setSendEmails(!sendEmails)} className={`relative w-12 h-6 rounded-full transition-colors ${sendEmails ? 'bg-lojack-red' : 'bg-gray-700'}`} data-testid="send-emails-toggle">
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${sendEmails ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4" data-testid="bulk-upload-results">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#111] rounded-xl p-4 text-center border border-gray-800"><p className="text-2xl font-bold text-white">{result.total}</p><p className="text-gray-500 text-xs mt-1">Totale righe</p></div>
                <div className="bg-[#111] rounded-xl p-4 text-center border border-lojack-blue-900"><p className="text-2xl font-bold text-lojack-blue-500">{result.created}</p><p className="text-gray-500 text-xs mt-1">Creati</p></div>
                <div className="bg-[#111] rounded-xl p-4 text-center border border-red-900"><p className="text-2xl font-bold text-red-500">{result.errors?.length || 0}</p><p className="text-gray-500 text-xs mt-1">Errori</p></div>
              </div>
              {result.emails_queued > 0 && (<div className="bg-blue-900/20 border border-blue-800 rounded-xl p-3 flex items-center gap-2"><Mail size={16} className="text-blue-400" /><p className="text-blue-300 text-sm">{result.emails_queued} email di benvenuto in coda per l'invio</p></div>)}
              {result.errors?.length > 0 && (<div className="bg-[#111] rounded-xl border border-gray-800 overflow-hidden"><p className="text-sm font-medium text-red-400 p-3 border-b border-gray-800">Dettaglio errori:</p><div className="max-h-48 overflow-auto">{result.errors.map((err, i) => (<div key={i} className="px-3 py-2 border-b border-gray-800/50 text-sm"><span className="text-gray-500">Riga {err.row}:</span>{' '}<span className="text-red-400">{err.error}</span></div>))}</div></div>)}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between p-5 border-t border-gray-700">
          {!result ? (
            <><button onClick={onClose} className="px-5 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">Annulla</button>
            <button onClick={handleUpload} disabled={!file || uploading} className="btn-lojack flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="bulk-upload-submit">
              {uploading ? <div className="spinner-small" /> : <Upload size={16} />}{uploading ? 'Caricamento...' : 'Carica Clienti'}</button></>
          ) : (
            <><button onClick={onClose} className="px-5 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">Chiudi</button>
            <button onClick={onSuccess} className="btn-lojack flex items-center gap-2" data-testid="bulk-upload-done"><Check size={16} />Fatto</button></>
          )}
        </div>
      </div>
    </div>
  );
};

const AddCustomerModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "", country: "IT", vehicle_plate: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/customers`, formData);
      toast.success("Cliente creato con successo");
      onSuccess();
    } catch (error) {
      toast.error("Errore nella creazione del cliente");
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-customer-modal">
        <h2 className="text-2xl font-bold text-white mb-6">Nuovo Cliente</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-400 text-sm mb-2">Nome</label><input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" required data-testid="input-first-name" /></div>
            <div><label className="block text-gray-400 text-sm mb-2">Cognome</label><input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" required data-testid="input-last-name" /></div>
          </div>
          <div><label className="block text-gray-400 text-sm mb-2">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" required data-testid="input-email" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-400 text-sm mb-2">Paese</label>
              <select value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" data-testid="select-country">
                {[['IT','Italia'],['FR','France'],['ES','Espana'],['DE','Deutschland'],['UK','United Kingdom'],['PT','Portugal'],['BE','Belgique / Belgie'],['NL','Nederland'],['PL','Polska'],['SE','Sverige'],['NO','Norge'],['FI','Suomi'],['DK','Danmark'],['CZ','Ceska republika'],['GR','Ellada'],['CH','Schweiz / Suisse'],['GBZ','Gibraltar'],['IRL','Ireland']]
                  .sort((a, b) => a[0] === 'IT' ? -1 : b[0] === 'IT' ? 1 : a[1].localeCompare(b[1]))
                  .map(([code, name]) => (
                  <option key={code} value={code}>{code} {name}</option>
                ))}
              </select>
            </div>
            <div><label className="block text-gray-400 text-sm mb-2">Targa Veicolo</label><input type="text" value={formData.vehicle_plate} onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" placeholder="AB123CD" data-testid="input-plate" /></div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 btn-lojack-outline">Annulla</button>
            <button type="submit" disabled={loading} className="flex-1 btn-lojack" data-testid="submit-customer-btn">{loading ? "Creazione..." : "Crea Cliente"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomersList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState(null);
  const [previewCustomerId, setPreviewCustomerId] = useState(null);
  const [syncingSfId, setSyncingSfId] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  // Fabrizio CR 07/08/2026 — Country divergence audit inline badge.
  // Set<customer.id> populated once at mount from the audit endpoint;
  // used to render an "⚠️ Paese cambiato post-firma" badge next to
  // affected rows. Cheap (single request, minimal payload) — see
  // ``routes/admin_country_divergence.py::country_divergence_flags``.
  const [divergentIds, setDivergentIds] = useState(new Set());
  const [divergentDetails, setDivergentDetails] = useState({});  // {customer_id: [snapshots]}
  // AbortController kept across renders so a NEW language click cancels
  // the still-in-flight request; prevents the "select 5 langs quickly
  // and end up showing the wrong one because requests resolved out of
  // order" race condition that produced the Bug Email Preview Dropdown
  // Timeout reported across multiple sessions.
  const previewAbortRef = useRef(null);
  const navigate = useNavigate();

  const fetchCustomers = async () => { try { setLoading(true); const r = await axios.get(`${API}/customers`); setCustomers(r.data); } catch { toast.error("Errore nel caricamento dei clienti"); } finally { setLoading(false); } };
  const sendEmail = async (cid) => { try { await axios.post(`${API}/customers/${cid}/send-welcome-email`); toast.success("Email di benvenuto inviata"); setEmailPreview(null); fetchCustomers(); } catch { toast.error("Errore nell'invio dell'email"); } };

  const openEmailPreview = async (cid, langOverride = null) => {
    // Cancel any in-flight preview request so the user sees the LATEST
    // language they picked, even on a rapid-fire dropdown change.
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }
    const controller = new AbortController();
    previewAbortRef.current = controller;

    try {
      setLoadingPreview(true);
      const qs = langOverride ? `?lang=${langOverride}` : "";
      const res = await axios.get(`${API}/customers/${cid}/email-preview${qs}`, {
        signal: controller.signal,
        timeout: 8000,
      });
      // If a NEWER request has already taken over, drop this stale result
      if (previewAbortRef.current !== controller) return;
      setEmailPreview(res.data);
      setPreviewLang(res.data.country);
      setPreviewCustomerId(cid);
    } catch (err) {
      // Swallow intentional cancellations — they're not user-visible errors
      if (axios.isCancel(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        return;
      }
      toast.error("Errore nel caricamento preview email");
    } finally {
      if (previewAbortRef.current === controller) {
        setLoadingPreview(false);
        previewAbortRef.current = null;
      }
    }
  };

  const changePreviewLang = (newLang) => {
    if (!previewCustomerId || newLang === previewLang) return;
    openEmailPreview(previewCustomerId, newLang);
  };
  const deleteCustomer = async (cid, c) => {
    // Admin delete — passes through DELETE /api/customers/{id} which
    // now requires:
    //   - admin Bearer token (provided via axios default header)
    //   - mandatory reason for audit trail
    //   - explicit override_lifecycle=true when contract_signed /
    //     card_activated / self_compilation_started
    // Fabrizio CR 23/02/2026 — replaces the legacy unauthenticated
    // hard-delete that allowed an activated-card customer to be
    // erased by a dealer.
    const cardActivated = c && ["activated", "active", "issued", "delivered"].includes(
      (c.card_status || "").toLowerCase()
    );
    const lifecycleLocked = c && (c.contract_signed || c.terms_accepted || cardActivated);
    const lockReason = !lifecycleLocked
      ? null
      : c.contract_signed
        ? "contratto firmato"
        : cardActivated
          ? "card wallet attivata"
          : "cliente ha accettato i termini e iniziato l'attivazione";
    const baseMsg = `Sei sicuro di voler eliminare questo cliente?`;
    const warn = lifecycleLocked
      ? `\n\n⚠️ ATTENZIONE: ${lockReason}. Confermando si registra un Admin Override nei log di compliance.`
      : "";
    if (!window.confirm(baseMsg + warn)) return;
    const reason = window.prompt(
      "Motivo dell'eliminazione (obbligatorio, salvato nei log di compliance):",
      lifecycleLocked ? "GDPR Art. 17 erasure request / Errore di registrazione" : "Errore di inserimento dati"
    );
    if (!reason || !reason.trim()) {
      toast.error("Motivo obbligatorio. Eliminazione annullata.");
      return;
    }
    try {
      await axios.delete(`${API}/customers/${cid}`, {
        params: {
          reason: reason.trim(),
          override_lifecycle: lifecycleLocked ? true : undefined,
        },
      });
      toast.success("Cliente eliminato (soft-delete, audit registrato)");
      fetchCustomers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 423 && detail) {
        toast.error(detail.message || "Eliminazione bloccata dal lifecycle guard", { duration: 7000 });
        return;
      }
      if (status === 401) {
        toast.error("Sessione admin scaduta — effettua di nuovo l'accesso");
        return;
      }
      toast.error(detail?.message || detail || "Errore nell'eliminazione del cliente");
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  // Fabrizio CR 07/08/2026 — Load country divergence flags once at mount.
  // Kept as a separate effect so the customers list renders IMMEDIATELY
  // even if the audit endpoint is slow. Failures are silent (badge just
  // doesn't appear — the customers list itself keeps working).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/audits/country-divergence`);
        if (cancelled) return;
        const ids = new Set((r.data?.items || []).map(i => i.customer_id));
        const detailsMap = {};
        (r.data?.items || []).forEach(i => {
          if (!detailsMap[i.customer_id]) detailsMap[i.customer_id] = [];
          detailsMap[i.customer_id].push({
            contract_country: i.contract_country_snapshot,
            signed_at: i.signed_at,
            days: i.days_since_signing,
          });
        });
        setDivergentIds(ids);
        setDivergentDetails(detailsMap);
      } catch {
        // Non-admin viewer or endpoint down — silently skip the badge.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const countryFlags = {
    IT:"🇮🇹", FR:"🇫🇷", ES:"🇪🇸", DE:"🇩🇪", UK:"🇬🇧", PT:"🇵🇹", BE:"🇧🇪", NL:"🇳🇱",
    CH:"🇨🇭", AT:"🇦🇹", PL:"🇵🇱", SE:"🇸🇪", NO:"🇳🇴", FI:"🇫🇮", DK:"🇩🇰", IE:"🇮🇪", IRL:"🇮🇪",
    GR:"🇬🇷", LU:"🇱🇺", CZ:"🇨🇿", SK:"🇸🇰", HU:"🇭🇺", RO:"🇷🇴", BG:"🇧🇬",
    SI:"🇸🇮", HR:"🇭🇷", EE:"🇪🇪", LV:"🇱🇻", LT:"🇱🇹", CY:"🇨🇾", MT:"🇲🇹", GBZ:"🇬🇮",
  };

  const closePreview = () => {
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
    setEmailPreview(null);
    setPreviewLang(null);
    setPreviewCustomerId(null);
    setLoadingPreview(false);
  };

  const syncCustomerToSalesforce = async (c) => {
    setSyncingSfId(c.id);
    try {
      const res = await axios.post(`${API}/salesforce/sync-customer/${c.id}`);
      const dup = res.data.sync_status === "duplicate";
      toast.success(
        `Salesforce: member number ${res.data.member_number}${dup ? " (cliente gia esistente)" : " assegnato"}`
      );
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync Salesforce fallito");
    } finally {
      setSyncingSfId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="customers-list">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-3xl font-bold text-white">Gestione Clienti</h1><p className="text-gray-400 mt-1">Lista clienti con servizio Protezione Veicolo</p></div>
        <div className="flex gap-3">
          <button onClick={fetchCustomers} className="btn-lojack-outline flex items-center gap-2" data-testid="refresh-customers-btn"><RefreshCw size={18} />Aggiorna</button>
          <button onClick={() => setShowBulkUpload(true)} className="btn-lojack-outline flex items-center gap-2" data-testid="bulk-upload-btn"><Upload size={18} />Upload Massivo</button>
          <button onClick={() => setShowAddModal(true)} className="btn-lojack flex items-center gap-2" data-testid="add-customer-btn"><Plus size={18} />Nuovo Cliente</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>
      ) : customers.length === 0 ? (
        <div className="wallet-card text-center py-12">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nessun cliente trovato</h3>
          <p className="text-gray-400 mb-6">Aggiungi il primo cliente o carica i dati demo</p>
          <button onClick={() => setShowAddModal(true)} className="btn-lojack"><Plus className="inline mr-2" size={18} />Aggiungi Cliente</button>
        </div>
      ) : (
        <div className="wallet-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Cliente</th><th>Paese</th><th>Targa</th><th>Email</th><th>Card</th><th>Termini</th><th>GDPR</th><th>Salesforce</th><th>Azioni</th></tr></thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-white truncate max-w-[18rem]" data-testid={`customer-name-${c.id}`}>
                            {getCustomerDisplayName(c) || <span className="italic text-gray-500">(senza nome)</span>}
                          </span>
                          <CustomerTypeBadge type={c.customer_type} />
                        </div>
                        <div className="text-sm text-gray-500">{c.email}</div>
                      </div>
                    </td>
                    <td>
                      <span className="text-2xl mr-2">{countryFlags[c.country]}</span>{c.country}
                      {divergentIds.has(c.id) && (
                        <span
                          className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded text-[10px] font-semibold uppercase tracking-wide"
                          title={(() => {
                            const details = divergentDetails[c.id] || [];
                            const snapshots = [...new Set(details.map(d => d.contract_country))].join(", ");
                            return `Paese cambiato post-firma contratto — snapshot originale: ${snapshots || "?"}. Il contratto firmato ha un paese diverso dall'anagrafica attuale.`;
                          })()}
                          data-testid={`country-divergence-badge-${c.id}`}
                        >
                          <AlertTriangle size={10} />
                          Paese cambiato
                        </span>
                      )}
                    </td>
                    <td className="font-mono">{c.vehicle_plate || "-"}</td>
                    <td><StatusBadge status={c.email_status} type="email" /></td>
                    <td><StatusBadge status={c.card_status} type="card" /></td>
                    <td>{c.terms_accepted ? <CheckCircle className="text-lojack-blue-500" size={20} /> : <span className="text-gray-500">-</span>}</td>
                    <td>{c.gdpr_preferences ? <span className="inline-flex items-center gap-1 px-2 py-1 bg-lojack-blue-500/20 text-lojack-blue-400 text-xs rounded-full"><Shield size={12} /> OK</span> : <span className="text-gray-500">-</span>}</td>
                    <td>
                      {c.salesforce_member_number ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full font-mono" title={c.salesforce_sync_status === "duplicate" ? "Gia presente in Salesforce CRM" : "Creato in Salesforce CRM"} data-testid={`sf-member-${c.id}`}>
                          <Cloud size={12} /> {c.salesforce_member_number}
                        </span>
                      ) : c.contract_signed ? (
                        <button onClick={() => syncCustomerToSalesforce(c)} disabled={syncingSfId === c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-800 hover:bg-blue-600/20 text-gray-400 hover:text-blue-400 text-xs rounded-full transition-colors disabled:opacity-50" title="Invia a Salesforce CRM" data-testid={`sf-sync-${c.id}`}>
                          {syncingSfId === c.id ? <RefreshCw size={12} className="animate-spin" /> : <Cloud size={12} />} Sync
                        </button>
                      ) : (
                        <span className="text-gray-500" title="Disponibile dopo la firma del contratto">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/wallet/${c.id}`)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title="Visualizza Card" data-testid={`view-card-${c.id}`}><Eye size={18} className="text-gray-400 hover:text-white" /></button>
                        <button onClick={() => setEditingCustomer(c)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title={c.contract_signed ? "Visualizza dati (firmato)" : "Modifica Cliente"} data-testid={`edit-customer-${c.id}`}><Pencil size={18} className={c.contract_signed ? "text-gray-600" : "text-gray-400 hover:text-lojack-clause-400"} /></button>
                        <button onClick={() => openEmailPreview(c.id)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title="Preview & Invia Email" data-testid={`send-email-${c.id}`}><Mail size={18} className="text-gray-400 hover:text-blue-400" /></button>
                        <button onClick={() => deleteCustomer(c.id, c)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title="Elimina" data-testid={`delete-customer-${c.id}`}><Trash2 size={18} className="text-gray-400 hover:text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {emailPreview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={closePreview}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-gray-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-700">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold text-white flex items-center gap-2"><Eye size={20} className="text-lojack-red" />Preview Email</h2>
                <p className="text-sm text-gray-400 mt-1">
                  A: <span className="text-white">{emailPreview.to}</span> &middot; Oggetto: <span className="text-white">{emailPreview.subject}</span>
                </p>
              </div>

              {/* Language switcher — lets marketing QA the same email in every locale.
                  NOT disabled during load: AbortController cancels the previous
                  request so the user can rapid-fire change locales without
                  getting "stuck" by a slow network round-trip. */}
              <div className="flex items-center gap-2 shrink-0">
                <label htmlFor="email-preview-lang" className="text-xs text-gray-500 uppercase tracking-wider">Lingua</label>
                <select
                  id="email-preview-lang"
                  value={previewLang || emailPreview.country}
                  onChange={(e) => changePreviewLang(e.target.value)}
                  className={`bg-[#0A0A0A] border border-gray-700 rounded-md px-2 py-1.5 text-white text-xs focus:border-lojack-red focus:outline-none cursor-pointer ${loadingPreview ? 'opacity-70' : ''}`}
                  data-testid="email-preview-lang-select"
                >
                  {(emailPreview.available_languages || [emailPreview.country]).map(code => {
                    const isOriginal = code === emailPreview.customer_country;
                    const flag = countryFlags[code] || "🏳️";
                    return (
                      <option key={code} value={code}>
                        {flag}&nbsp;&nbsp;{code}{isOriginal ? " ★ cliente" : ""}
                      </option>
                    );
                  })}
                </select>
                {loadingPreview && <RefreshCw size={12} className="animate-spin text-gray-500" />}
                <button onClick={closePreview} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-testid="email-preview-x"><X size={20} className="text-gray-400" /></button>
              </div>
            </div>

            {/* Banner: alert when previewing a language different from the customer's actual country */}
            {previewLang && previewLang !== emailPreview.customer_country && (
              <div className="px-5 py-2 bg-lojack-clause-500/10 border-b border-lojack-clause-500/30 text-lojack-clause-300 text-xs flex items-center gap-2" data-testid="email-preview-lang-warning">
                <span>⚠️</span>
                <span>
                  Anteprima nella lingua <span className="font-bold">{previewLang}</span>. Il cliente "{emailPreview.customer_name}" è registrato in <span className="font-bold">{emailPreview.customer_country}</span> — l'invio reale userà quella lingua.
                </span>
              </div>
            )}

            <div className="flex-1 overflow-auto p-1" data-testid="email-preview-content">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm gap-2">
                  <RefreshCw size={14} className="animate-spin" /> Caricamento anteprima…
                </div>
              ) : (
                <iframe
                  key={previewLang /* forces a remount on language change */}
                  ref={(el) => { if (el && emailPreview.html) { const blob = new Blob([emailPreview.html], { type: 'text/html;charset=utf-8' }); el.src = URL.createObjectURL(blob); } }}
                  title="Email Preview"
                  className="w-full border-0 rounded-lg"
                  style={{ minHeight: '500px', height: '60vh', background: '#0A0A0A' }}
                />
              )}
            </div>
            <div className="flex items-center justify-between p-5 border-t border-gray-700">
              <button onClick={closePreview} className="px-5 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" data-testid="email-preview-close">Chiudi</button>
              <button onClick={() => { const cId = customers.find(c => c.email === emailPreview.to)?.id; if (cId) sendEmail(cId); }} className="btn-lojack flex items-center gap-2" data-testid="email-preview-send"><Send size={16} />Invia Email</button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && <AddCustomerModal onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchCustomers(); }} />}
      {showBulkUpload && <BulkUploadModal onClose={() => setShowBulkUpload(false)} onSuccess={() => { setShowBulkUpload(false); fetchCustomers(); }} />}
      {editingCustomer && <EditCustomerModal customer={editingCustomer} onClose={() => setEditingCustomer(null)} onSuccess={() => { setEditingCustomer(null); fetchCustomers(); }} />}
    </div>
  );
};

export default CustomersList;
